import React from 'react';
import { Row } from 'react-flexbox-grid';
import { Checkbox } from 'antd';
import tick from '../../../assets/Tick.png';

const ipscss = {
    marginLeft: '0px',
    marginRight: '0px'
};

/**
 * PreEFormAssetMapping
 *
 * Renders a single asset row with a checkbox.
 *
 * Props
 * ──────────────────────────────
 * asset                 – asset object with selection state
 * currentFormId         – ID of the current form
 * onChangeAssetId       – called when checkbox is toggled
 * saveVisibleText       – legacy: truthy when in edit mode (inverted in parent)
 * disabled              – explicit disable flag (takes precedence)
 * preEFormMapping       – parent mapping object
 * setActive             – accordion state setter
 * setHeight             – accordion height setter
 * attestView            – true when in viewer/attestation mode
 * onAssetCheckboxChange – viewer highlight callback (attestView only)
 */
const PreEFormAssetMapping = (props) => {
    const {
        asset,
        currentFormId,
        onChangeAssetId,
        saveVisibleText,
        disabled = false,
        preEFormMapping,
        setActive,
        setHeight,
        attestView = false,
        onAssetCheckboxChange,
    } = props;

    // Determine if this asset is editable (belongs to current form or unassigned)
    const isEditable = !asset.formId || asset.formId === 0 || asset.formId === currentFormId;

    // Checkbox is disabled if:
    // 1. Explicit disabled prop is true, OR
    // 2. saveVisibleText is truthy (inverted legacy semantics)
    const isCheckboxDisabled = disabled || saveVisibleText;

    // To make the intent explicit and order-independent, nextSelected is
    // computed once up front. onChangeAssetId still receives the original
    // asset (localOnChangeAssetId derives direction via !asset.selected
    // from prevMappings internally). onAssetCheckboxChange receives an
    // enriched copy with selected set to the unambiguous next value, so
    // handleAssetCheckboxChange always reads the correct pre-toggle intent
    // regardless of call order or future refactors.
    // ─────────────────────────────────────────────────────────────
    const handleCheckboxChange = (e) => {
        // Prevent ALL event propagation to avoid triggering form submission or modal close
        if (e) {
            e.stopPropagation();
            if (e.nativeEvent) {
                e.nativeEvent.stopImmediatePropagation();
                e.nativeEvent.preventDefault();
            }
        }

        // ✅ Compute the intended next state once, before either callback fires.
        //    Both callbacks can now use it without relying on call order.
        const nextSelected = !asset.selected;

        if (isEditable && typeof onChangeAssetId === 'function') {
            // onChangeAssetId (→ localOnChangeAssetId) derives toggle direction
            // from !asset.selected inside its own prevMappings updater, so the
            // original asset object is the correct argument here.
            onChangeAssetId(asset, preEFormMapping.ipsLocationId, setActive, setHeight);
        }

        // In attestView mode, also notify the viewer for highlighting.
        if (attestView && typeof onAssetCheckboxChange === 'function') {
            // ✅ Pass an enriched copy with the explicit next state so
            //    handleAssetCheckboxChange always receives the correct
            //    pre-toggle value (wasChecked = !!asset.selected) regardless
            //    of call order or batching.
            onAssetCheckboxChange({ ...asset, selected: nextSelected });
        }
    };

    // Prevent row clicks from bubbling
    const handleRowClick = (e) => {
        e.stopPropagation();
    };

    return (
        <Row
            style={ipscss}
            className={`pre-status--row ${asset.selected && asset.formId === currentFormId ? 'selected' : ''} ${isEditable ? '' : 'signed'}`}
            onClick={handleRowClick}
        >
            <div className="tasklist--checkbox">
                {isEditable ? (
                    <Checkbox
                        disabled={isCheckboxDisabled}
                        checked={!!asset.selected}
                        onChange={handleCheckboxChange}
                    />
                ) : (
                    <span className="tick--signed">
                        <img src={tick} alt="Tick" />
                    </span>
                )}
            </div>

            <div className="col-xs">
                {asset.deleteFlag === 'P' ? (
                    <span style={{ color: 'red' }}>{asset.assetName}</span>
                ) : asset.deleteFlag === 'B' ? (
                    <b>{asset.assetName}</b>
                ) : (
                    asset.assetName
                )}
            </div>

            {!attestView && (
                <>
                    <div className="col-xs" style={{ textAlign: 'center' }}>
                        {asset.deleteFlag === 'P' ? (
                            <span style={{ color: 'red' }}>VERIFY IPS DATA</span>
                        ) : asset.deleteFlag === 'B' ? (
                            <b>VERIFY IPS DATA</b>
                        ) : (
                            asset.assetGroupName
                        )}
                    </div>
                    <div className="col-xs" style={{ textAlign: 'center' }}>
                        {asset.assetStatus}
                    </div>
                </>
            )}
        </Row>
    );
};

export default PreEFormAssetMapping;